import mapboxgl from 'mapbox-gl';
import axios from 'axios/index';
import { parse } from 'qs';
import PlanMap from './PlanMap/PlanMap';
import { syncFavorite } from '../helpers/favorites';
import PlanVr from './PlanVr/PlanVr';
import { replaceUrl, pushUrl } from '../helpers/query-string';
import Modal, { initModalPopstate } from '../UiKit/Elements/Modal/Modal';
import { initDeveloperQuestion } from '../DeveloperQuestionPopup/DeveloperQuestionPopup';
import { initViewHeight } from '../helpers/view-height';
import { initPhone } from '../ContactsActions/ContactsPhoneButton/ContactsPhoneButton';
import { initPlanCanvas } from './PlanCanvas/PlanCanvas';
import { initFixedContacts } from '../Building/BuildingContactsLayout/BuildingContactsLayout';
import { initClipper } from '../Clipper/Clipper';
import { initShownAnalytics } from '../helpers/analytics';

const MODAL_ID = 'plans-modal';

class PlanModal {
  constructor(params, options = {}) {
    this.modalId = Number(params.modalId);
    this.modalIds = window.params.modalsData;
    this.requestUrl = params.requestUrl;
    this.openedModalKey = params.openedModalKey;
    this.additionalParams = options.additionalParams || {};
    this.currentIndex = Object.keys(this.modalIds).find((key) => {
      return this.modalIds[key].id === this.modalId;
    });
    this.options = options;

    this.initParams();
    this.renderModal();
  }

  initParams() {
    if (this.modalIds[this.currentIndex]) {
      const url = this.modalIds[this.currentIndex].modalUrl;
      const [path, responseParams] = url.split('?');
      this.path = path;
      this.responseParams = parse(responseParams);
    } else {
      // fix for opening modal not from modalsData of current page (example: opened from map)
      const locationSearch = window.location.search;
      this.modalIds = [
        {
          id: this.modalId,
          modalUrl: `${window.params.baseUrl}${locationSearch}`,
        },
      ];
      this.currentIndex = 0;
      this.path = window.params.baseUrl;
      this.responseParams = parse(locationSearch.split('?')[1]);
    }
  }

  modalRequest = (id = this.modalId) => {
    this.modalId = id;
    return axios({
      url: `${this.requestUrl}/${this.modalId}`,
      method: 'get',
      headers: {
        'X-CSRF-Token': window.csrf,
        'Content-type': 'application/json',
      },
      withCredentials: true,
    });
  };

  renderModal() {
    this.modal = new Modal(MODAL_ID, {
      request: this.modalRequest,
      modalIds: this.modalIds,
      currentIndex: this.currentIndex,
      onResponse: this.onResponse,
      onUpdate: this.onUpdate,
      onShow: () => {
        this.onShow();
        pushUrl({
          path: this.path,
          params: Object.assign(this.responseParams, this.additionalParams),
          hash: '',
        });
      },
      onClose: () => {
        this.map?.unmount();
        Object.keys(this.additionalParams).forEach((key) => {
          this.additionalParams[key] = null;
        });
        replaceUrl({
          path: this.path,
          params: Object.assign(this.responseParams, this.additionalParams, {
            [this.openedModalKey]: null,
          }),
          hash: '',
        });
        ga('set', 'contentGroup3', 'flats/list');
        ga('send', 'pageview');
      },
    });

    initModalPopstate(this.modal);
  }

  fullscreenConfig = () => {
    return {
      onFullscreen: () => {
        this.modal.removeEventListeners();
        this.modal.node.classList.remove('UIModal');
      },
      offFullscreen: () => {
        this.modal.addEventListeners();
        initViewHeight();
        this.modal.node.classList.add('UIModal');
      },
    };
  };

  onResponse = (response, node) => {
    node.innerHTML = response.data.html.modal;
    window.params.developerQuestionUrl = response.data.params.developerQuestionUrl;
    const planVRNode = document.getElementById('plan-vr');
    if (planVRNode) {
      new window.Waypoint({
        element: planVRNode,
        handler: () => {
          if (!this.planVr) {
            this.planVr = new PlanVr(response.data.params.VRLink, this.fullscreenConfig());
          }
        },
        offset: function offset() {
          return 1.35 * this.element.clientHeight;
        },
        context: window.isMobile ? this.modal.content : this.modal.node
      });
    }
    if (mapboxgl.supported()) {
      const mapNode = document.getElementById('map');
      new window.Waypoint({
        element: mapNode,
        handler: () => {
          if (!this.map) {
            this.map = new PlanMap(response.data.params, {
              updateModal: this.modal.updateModal,
              unmountPlanVrControls: this.planVr?.unmountControls,
              ...this.fullscreenConfig(),
            });
          }
        },
        offset: function offset() {
          return 1.35 * this.element.clientHeight;
        },
        context: window.isMobile ? this.modal.content : this.modal.node
      });
    }
  };

  onShow() {
    syncFavorite();
    initPhone(this.modal.node);
    initPlanCanvas(this.modal.node);
    initFixedContacts(this.modal.content);
    initClipper(this.modal.node);

    const developerQuestionOptions = {
      beforeShow: this.modal.removeEventListeners,
      onClose: this.modal.addEventListeners,
    };

    if (this.openedModalKey === 'flatId') {
      developerQuestionOptions.isQuestionFromFlats = true;
    }

    initShownAnalytics(this.modal.node);
    initDeveloperQuestion(this.modal.node, developerQuestionOptions);
    ga('set', 'contentGroup3', 'flats/view');
    ga('send', 'pageview');
  }

  onUpdate = (id, response, additionalParams = {}) => {
    this.currentIndex = id;
    this.initParams();
    replaceUrl({
      path: this.path,
      params: Object.assign(this.responseParams, additionalParams, {
        [this.openedModalKey]: this.modalId,
      }),
      hash: '',
    });
    this.updateContent(response.data.html);
    this.planVr = new PlanVr(response.data.params.VRLink, this.fullscreenConfig());
    if (this.map) {
      this.map.unmount();
      this.map.params = response.data.params;
      this.map.getData();
      this.map.map?.resize();
      this.map.options.unmountPlanVrControls = this.planVr?.unmountControls;
    }
    window.params.developerQuestionUrl = response.data.params.developerQuestionUrl;
    this.onShow();
  };

  updateContent(data) {
    const mainNode = this.modal.node.querySelector('#plan-main');
    const creditInstallmentsNode = this.modal.node.querySelector('#plan-credit-installments');
    const layoutsNode = this.modal.node.querySelector('#plan-layouts');
    const sidebarNode = this.modal.node.querySelector('#plan-sidebar');

    mainNode.innerHTML = data.main;
    creditInstallmentsNode.innerHTML = data.creditInstallments;
    layoutsNode.innerHTML = data.layouts || '';
    sidebarNode.innerHTML = data.sidebar;

    try {
      this.modal.node.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      this.modal.node.scrollTo(0, 0);
    }
  }
}

export const attachListenersPlanCards = (searchParams, key, apiUrl) => {
  const openedModalKey = key || 'layout_id';
  const modalId = searchParams[openedModalKey];
  const requestUrl = apiUrl || '/api/layout-modal';

  if (modalId) {
    new PlanModal(
      {
        modalId,
        requestUrl,
        openedModalKey,
      }
    );
  }

  const cards = document.querySelectorAll('[data-plans-card]');
  [].forEach.call(cards, (card) => {
    const favoriteButton = card.querySelector('[data-favorites]');
    const cardId = card.getAttribute('data-plans-card');

    card.addEventListener('click', (event) => {
      event.preventDefault();
      if (favoriteButton?.contains(event.target)) return;

      new PlanModal(
        {
          modalId: cardId,
          requestUrl,
          openedModalKey,
        }
      );
    });
  });
};

export default PlanModal;
